import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { DragDropContext, Droppable, Draggable, DraggableProvided, DropResult } from 'react-beautiful-dnd';

interface Document {
  _id: string;
  title: string;
  content: string;
  required: boolean;
  order: number;
  category: 'onboarding' | 'pre-application' | 'policies' | 'agreement' | 'guides';
  audience?: 'all' | 'counsellor' | 'administrator';
  createdAt: string;
  updatedAt: string;
}

interface DocumentsProps {
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const Documents: React.FC<DocumentsProps> = ({ onSuccess, onError }) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'onboarding' | 'pre-application' | 'policies' | 'agreement' | 'guides'>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState<Partial<Document>>({
    title: '',
    content: '',
    required: true,
    category: 'onboarding'
  });
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = activeTab !== 'all' ? `?category=${activeTab}` : '';
      const response = await api.get(`/api/documents${params}`);
      setDocuments(response.data);
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  }, [onError, activeTab]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCurrentDocument(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setCurrentDocument(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleOpenDialog = (document?: Document) => {
    if (document) {
      setCurrentDocument(document);
    } else {
      setCurrentDocument({
        title: '',
        content: '',
        required: true,
        category: 'onboarding'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    // Reset to default state when closing
    setCurrentDocument({
      title: '',
      content: '',
      required: true,
      category: 'onboarding'
    });
  };

  const handleOpenDeleteDialog = (id: string) => {
    setDocumentToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDocumentToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleSaveDocument = async () => {
    try {
      setLoading(true);
      
      if (!currentDocument.title || !currentDocument.content) {
        onError('Title and content are required');
        setLoading(false);
        return;
      }
      
      if (currentDocument._id) {
        // Update existing document
        await api.put(`/api/documents/${currentDocument._id}`, currentDocument);
        onSuccess('Document updated successfully');
      } else {
        // Create new document
        await api.post('/api/documents', currentDocument);
        onSuccess('Document created successfully');
      }
      
      await fetchDocuments();
      handleCloseDialog();
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to save document');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;
    
    try {
      setLoading(true);
      await api.delete(`/api/documents/${documentToDelete}`);
      onSuccess('Document deleted successfully');
      await fetchDocuments();
      handleCloseDeleteDialog();
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to delete document');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveDocument = async (documentId: string, direction: 'up' | 'down') => {
    const currentIndex = documents.findIndex(doc => doc._id === documentId);
    if (
      (direction === 'up' && currentIndex === 0) || 
      (direction === 'down' && currentIndex === documents.length - 1)
    ) {
      return; // Can't move further
    }
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const newDocuments = [...documents];
    const [movedItem] = newDocuments.splice(currentIndex, 1);
    newDocuments.splice(newIndex, 0, movedItem);
    
    // Update order property for all documents
    const reorderedDocuments = newDocuments.map((doc, index) => ({
      ...doc,
      order: index
    }));
    
    setDocuments(reorderedDocuments);
    
    try {
      const documentIds = reorderedDocuments.map(doc => doc._id);
      await api.post('/api/documents/reorder', { documentIds });
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to reorder documents');
      await fetchDocuments(); // Refresh from server on error
    }
  };

  const onDragEnd = async (result: DropResult) => {
    // Dropped outside the list
    if (!result.destination) {
      return;
    }
    
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    
    if (sourceIndex === destinationIndex) {
      return;
    }
    
    const newDocuments = [...documents];
    const [movedItem] = newDocuments.splice(sourceIndex, 1);
    newDocuments.splice(destinationIndex, 0, movedItem);
    
    // Update order property for all documents
    const reorderedDocuments = newDocuments.map((doc, index) => ({
      ...doc,
      order: index
    }));
    
    setDocuments(reorderedDocuments);
    
    try {
      const documentIds = reorderedDocuments.map(doc => doc._id);
      await api.post('/api/documents/reorder', { documentIds });
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to reorder documents');
      await fetchDocuments(); // Refresh from server on error
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" className="theme-text-primary">
          Document Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Document
        </Button>
      </Box>
      
      <Card sx={{ mb: 4, backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <CardContent>
          <Typography variant="body1" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Manage documents for onboarding, pre-application, policies & procedures, help &amp; guides, and counsellor agreements.
            Onboarding documents require counsellor confirmation. Agreements must be signed by counsellors after induction.
            Help &amp; Guides appear on the Policies &amp; Procedures page and can be restricted by audience.
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Drag and drop documents to change their order, or use the up/down arrows.
          </Typography>
        </CardContent>
      </Card>

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{
          mb: 3,
          '& .MuiTab-root': { color: 'rgba(255, 255, 255, 0.6)', textTransform: 'none', fontWeight: 500 },
          '& .Mui-selected': { color: 'rgba(100, 181, 246, 1) !important' },
          '& .MuiTabs-indicator': { backgroundColor: 'rgba(100, 181, 246, 0.8)' }
        }}
      >
        <Tab label="All" value="all" />
        <Tab label="Onboarding" value="onboarding" />
        <Tab label="Pre-Application" value="pre-application" />
        <Tab label="Policies & Procedures" value="policies" />
        <Tab label="Counsellor Agreement" value="agreement" />
        <Tab label="Help & Guides" value="guides" />
      </Tabs>
      
      {loading && !dialogOpen && !deleteDialogOpen && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {!loading && documents.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No documents have been added yet. Click "Add Document" to create your first document.
        </Alert>
      )}
      
      {!loading && documents.length > 0 && (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="documents">
            {(provided) => (
              <TableContainer 
                component={Paper} 
                ref={provided.innerRef} 
                {...provided.droppableProps}
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                  backdropFilter: 'blur(10px)', 
                  border: '1px solid rgba(255, 255, 255, 0.1)' 
                }}
              >
                <Table>
                  <TableHead sx={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}>
                    <TableRow>
                      <TableCell width="5%" sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>#</TableCell>
                      <TableCell width="25%" sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Title</TableCell>
                      <TableCell width="15%" sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Category</TableCell>
                      <TableCell width="10%" sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Required</TableCell>
                      <TableCell width="15%" sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Last Updated</TableCell>
                      <TableCell width="30%" align="right" sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documents.map((document, index) => (
                      <Draggable key={document._id} draggableId={document._id} index={index}>
                        {(provided: DraggableProvided) => (
                          <TableRow
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            hover
                          >
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>{index + 1}</TableCell>
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>{document.title}</TableCell>
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                              {document.category === 'pre-application' ? 'Pre-Application' : 
                               document.category === 'policies' ? 'Policies & Procedures' : 
                               document.category === 'guides' ? 'Help & Guides' :
                               document.category === 'agreement' ? 'Counsellor Agreement' : 'Onboarding'}
                            </TableCell>
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                              {document.required ? 'Yes' : 'No'}
                            </TableCell>
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                              {new Date(document.updatedAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                              <IconButton 
                                size="small" 
                                onClick={() => handleMoveDocument(document._id, 'up')}
                                disabled={index === 0}
                                sx={{ color: 'rgba(100, 181, 246, 0.9)', '&:hover': { color: 'rgba(100, 181, 246, 1)' }, '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' } }}
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={() => handleMoveDocument(document._id, 'down')}
                                disabled={index === documents.length - 1}
                                sx={{ color: 'rgba(100, 181, 246, 0.9)', '&:hover': { color: 'rgba(100, 181, 246, 1)' }, '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' } }}
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                              <IconButton 
                                onClick={() => handleOpenDialog(document)}
                                sx={{ color: 'rgba(100, 181, 246, 0.9)', '&:hover': { color: 'rgba(100, 181, 246, 1)' } }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton 
                                onClick={() => handleOpenDeleteDialog(document._id)}
                                sx={{ color: 'rgba(239, 83, 80, 0.9)', '&:hover': { color: 'rgba(239, 83, 80, 1)' } }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Droppable>
        </DragDropContext>
      )}
      
      {/* Add/Edit Document Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>
          {currentDocument._id ? 'Edit Document' : 'Add Document'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label="Document Title"
            type="text"
            fullWidth
            value={currentDocument.title || ''}
            onChange={handleInputChange}
            required
            sx={{ 
              mb: 2,
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)'
              },
              '& .MuiOutlinedInput-root': {
                color: 'rgba(255, 255, 255, 0.9)',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.23)'
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)'
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(100, 181, 246, 0.7)'
                }
              }
            }}
          />
          
          <FormControl 
            fullWidth 
            sx={{ 
              mb: 2,
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)'
              },
              '& .MuiOutlinedInput-root': {
                color: 'rgba(255, 255, 255, 0.9)',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.23)'
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)'
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(100, 181, 246, 0.7)'
                }
              },
              '& .MuiSelect-icon': {
                color: 'rgba(255, 255, 255, 0.7)'
              }
            }}
          >
            <InputLabel>Category</InputLabel>
            <Select
              name="category"
              value={currentDocument.category || 'onboarding'}
              onChange={(e) => {
                const newCategory = e.target.value as 'onboarding' | 'pre-application' | 'policies' | 'agreement' | 'guides';
                setCurrentDocument(prev => ({
                  ...prev,
                  category: newCategory,
                  required: newCategory === 'policies' || newCategory === 'guides' ? false : prev.required
                }));
              }}
              label="Category"
            >
              <MenuItem value="onboarding">Onboarding</MenuItem>
              <MenuItem value="pre-application">Pre-Application</MenuItem>
              <MenuItem value="policies">Policies & Procedures</MenuItem>
              <MenuItem value="guides">Help & Guides</MenuItem>
              <MenuItem value="agreement">Counsellor Agreement</MenuItem>
            </Select>
          </FormControl>

          {currentDocument.category === 'guides' && (
            <FormControl
              fullWidth
              sx={{
                mb: 2,
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiOutlinedInput-root': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'rgba(100, 181, 246, 0.7)' }
                },
                '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.7)' }
              }}
            >
              <InputLabel>Audience</InputLabel>
              <Select
                name="audience"
                value={currentDocument.audience || 'all'}
                onChange={(e) => setCurrentDocument(prev => ({
                  ...prev,
                  audience: e.target.value as 'all' | 'counsellor' | 'administrator'
                }))}
                label="Audience"
              >
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="counsellor">Counsellors</MenuItem>
                <MenuItem value="administrator">Administrators</MenuItem>
              </Select>
            </FormControl>
          )}
          
          <FormControlLabel
            control={
              <Switch
                checked={currentDocument.required || false}
                onChange={handleSwitchChange}
                name="required"
                color="primary"
              />
            }
            label="Required (counsellor must acknowledge)"
            sx={{ mb: 2, '& .MuiFormControlLabel-label': { color: 'rgba(255, 255, 255, 0.9)' } }}
          />
          
          <TextField
            margin="dense"
            name="content"
            label="Document Content"
            multiline
            rows={15}
            fullWidth
            value={currentDocument.content || ''}
            onChange={handleInputChange}
            required
            helperText="You can use markdown formatting in the document content"
            sx={{
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)'
              },
              '& .MuiOutlinedInput-root': {
                color: 'rgba(255, 255, 255, 0.9)',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.23)'
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)'
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(100, 181, 246, 0.7)'
                }
              },
              '& .MuiFormHelperText-root': {
                color: 'rgba(255, 255, 255, 0.6)'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveDocument} 
            color="primary" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this document? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteDocument} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Documents;
